import {
  Box,
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type ExpenseBudgetItem,
  type ExpenseBudgetItemQuery,
  ReturnGlobalCreditDocument,
  type ReturnGlobalCreditMutation,
  type ReturnGlobalCreditMutationVariables,
  useExpenseBudgetItemQuery,
} from "@src/__generated__/graphql";
import { TransferGlobalCreditModal } from "@src/components/modules/credit-account/global-account-detail/components/transfer-credit-modal";
import { TransferGlobalCreditModalStore } from "@src/components/modules/credit-account/global-account-detail/components/transfer-credit-modal/store";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  ColoredAmount,
  DataTable,
  Link,
  LoadingOverlay,
} from "@src/components/ui-kit";
import { NumberInput } from "@src/components/ui-kit/NumberInput";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { onError } from "@src/utils/apollo";
import {
  currency,
  dateTime,
  denominate,
  nominate,
} from "@src/utils/formatters";
import { required } from "@src/utils/forms/validators";
import { useStore } from "@src/utils/hooks";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState } from "formstate";
import { kebabCase } from "lodash";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { useMemo } from "react";
import CompletedSection, {
  type CompletedSectionProps,
} from "./CompletedSection";
import IncompletedSection, {
  type IncompletedSectionProps,
} from "./IncompletedSection";
import { ExpenseItem } from "./models";
import TableExpenseStats from "./TableExpenseStats";

export type TOnCompletePayload = Parameters<
  IncompletedSectionProps["onComplete"]
>[0];
export type TOnReopenPayload = Parameters<CompletedSectionProps["onReopen"]>[0];

interface ModalCompleteExpenseProps extends UseModalProps {
  id: ExpenseBudgetItem["id"];
  projectId?: string;
  onAction?: (payload?: TOnCompletePayload | TOnReopenPayload) => void;
}

const ModalCompleteExpense = ({
  id,
  onAction,
  projectId,
  ...props
}: ModalCompleteExpenseProps) => {
  const router = useRouter();
  const appStore = useStore();
  const {
    transferCreditModalStore,
    returnCreditDisclosure,
    returnCreditMutation,
    amountField,
    isReturnCreditLoading,
  } = useLocalObservable(() => ({
    transferCreditModalStore: new TransferGlobalCreditModalStore(
      appStore,
      onAction,
    ),
    returnCreditDisclosure: new DisclosureState<Row>(),
    returnCreditMutation: new MutationHelper<
      ReturnGlobalCreditMutation,
      ReturnGlobalCreditMutationVariables
    >(ReturnGlobalCreditDocument),
    get isReturnCreditLoading() {
      return returnCreditMutation.isLoading.value;
    },
    amountField: new FieldState("0").validators(required),
  }));

  const { data, loading, refetch } = useExpenseBudgetItemQuery({
    notifyOnNetworkStatusChange: true,
    variables: { id },
    skip: !id || !props.isOpen,
    ...onError(),
  });

  const expenseItem = useMemo(() => {
    if (data?.expenseBudgetItem) {
      return new ExpenseItem(data.expenseBudgetItem);
    }

    return undefined;
  }, [data]);

  const handleAction = (data: TOnCompletePayload | TOnReopenPayload) => {
    expenseItem?.setCompletedAt(data.completed_at);
    expenseItem?.updateStats(data.stats);
    onAction?.(data);
  };

  const handleSubmitTransferCredit = () => {
    refetch();
    onAction?.();
  };

  const handleCloseReturnCreditModal = () => {
    if (isReturnCreditLoading) return;

    amountField.reset();
    returnCreditDisclosure.onClose();
  };

  const handleReturnCredit = async (row: Row) => {
    const validation = await amountField.validate();
    if (validation.hasError) return;

    const amount = Number(validation.value);

    const [, error] = await returnCreditMutation.mutate({
      amount: denominate(
        amount,
        appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ),
      expense_budget_item_id: row.expenseBudgetItem.id,
      global_credit_account_id: row.globalCreditAccount.id,
      credit_account_operation_id: row.credit_account_operation_id,
    });

    if (error) return;

    appStore.UIStore.toast({
      status: "success",
      title: t`Credit returned successfully`,
    });

    refetch();
    onAction?.();
    handleCloseReturnCreditModal();
  };

  const expenseCreditColumns = useMemo<BodyColumnDef<Row>[]>(() => {
    const handleBudgetItemClick = (row: Row) => {
      props.onClose();
      router.push({
        pathname: "/projects/detail",
        query: { id: projectId },
        hash: kebabCase(row.expenseBudgetItem?.title),
      });
    };

    return [
      {
        id: "date",
        meta: { label: t`Date` },
        header: () => t`Date`,
        cell: ({ row }) =>
          row.original.date ? dateTime.format(new Date(row.original.date)) : "",
      },
      {
        id: "brandCreditAccount",
        meta: { label: t`Credit account` },
        header: () => t`Credit account`,
        cell: ({ row }) => (
          <Link
            href={{
              pathname: "/credit-accounts/global-detail",
              query: { id: row.original.globalCreditAccount.id },
            }}
          >
            {row.original.globalCreditAccount.title}
          </Link>
        ),
      },
      {
        id: "budgetItem",
        meta: { label: t`Budget item` },
        header: () => t`Budget item`,
        cell: ({ row }) => (
          <Button
            onClick={() => handleBudgetItemClick(row.original)}
            variant="link"
          >
            {row.original.expenseBudgetItem?.title}
          </Button>
        ),
      },
      {
        id: "amount",
        meta: { label: t`Amount` },
        header: () => t`Amount`,
        cell: ({ row }) => (
          <ColoredAmount value={row.original.credit}>
            {currency.formatByWorkspace(row.original.credit)}
          </ColoredAmount>
        ),
      },
      {
        id: ACTIONS_COLUMN_ID,
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => (
          <Button
            onClick={() => {
              returnCreditDisclosure.onOpen(row.original);
            }}
            size="sm"
            variant="outline"
          >
            <Trans>Return credit</Trans>
          </Button>
        ),
      },
    ];
  }, [projectId, props, returnCreditDisclosure, router]);

  return (
    <Modal {...props} size="5xl" trapFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Expenses</Trans>{" "}
          {expenseItem?.title ? `(${expenseItem.title})` : ""}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pos="relative">
          <LoadingOverlay isLoading={loading} position="absolute" minH="200px">
            {expenseItem && (
              <React.Fragment>
                <TableExpenseStats id={id} expenseItem={expenseItem} />

                <Box mt="6">
                  <DataTable>
                    <DataTable.NavBar>
                      <HStack justify="space-between" py="4">
                        <Heading size="sm">
                          <Trans>Expense credit accounts</Trans>
                        </Heading>
                        <Button
                          onClick={
                            transferCreditModalStore.getCreditModalDisclosure
                              .onOpen
                          }
                          size="sm"
                        >
                          <Trans>Get credit</Trans>
                        </Button>
                      </HStack>
                    </DataTable.NavBar>
                    <DataTable.Body
                      data={
                        data?.expenseBudgetItem?.globalPrepaymentStats ?? []
                      }
                      columns={expenseCreditColumns}
                      tableKey="expense-global-credit-accounts"
                    />
                    {/* return credit modal */}
                    <Modal
                      isCentered
                      isOpen={returnCreditDisclosure.isOpen}
                      onClose={handleCloseReturnCreditModal}
                      size="md"
                      trapFocus={false}
                    >
                      <ModalOverlay />
                      <ModalContent>
                        <ModalHeader>
                          <Trans>Return credit</Trans>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          {returnCreditDisclosure.additionalData && (
                            <>
                              <Box mb="4">
                                <Heading mb="2" size="sm">
                                  <Trans>Credit account</Trans>
                                </Heading>
                                <Box>
                                  {
                                    returnCreditDisclosure.additionalData
                                      .globalCreditAccount.title
                                  }
                                </Box>
                              </Box>
                              <Box mb="4">
                                <Heading mb="2" size="sm">
                                  <Trans>Available credit</Trans>
                                </Heading>
                                <ColoredAmount
                                  value={
                                    returnCreditDisclosure.additionalData.credit
                                  }
                                >
                                  {currency.formatByWorkspace(
                                    returnCreditDisclosure.additionalData
                                      .credit,
                                  )}
                                </ColoredAmount>
                              </Box>
                              <NumberInput
                                currency
                                label={t`Amount to return`}
                                maxValue={nominate(
                                  Number(
                                    returnCreditDisclosure.additionalData
                                      .credit,
                                  ),
                                  appStore.workspaceStore.settings?.currency
                                    .denomination ?? 2,
                                )}
                                minValue={0}
                                onChange={amountField.onChange}
                                value={amountField.value}
                                error={amountField.error}
                              />
                            </>
                          )}
                        </ModalBody>
                        <ModalFooter>
                          <Button
                            colorScheme="grey"
                            isLoading={isReturnCreditLoading}
                            onClick={handleCloseReturnCreditModal}
                            variant="outline"
                          >
                            <Trans>Cancel</Trans>
                          </Button>
                          <Button
                            ml={2}
                            isLoading={isReturnCreditLoading}
                            onClick={() => {
                              if (!returnCreditDisclosure.additionalData)
                                return;
                              handleReturnCredit(
                                returnCreditDisclosure.additionalData,
                              );
                            }}
                          >
                            <Trans>Return credit</Trans>
                          </Button>
                        </ModalFooter>
                      </ModalContent>
                    </Modal>
                  </DataTable>
                </Box>

                <TransferGlobalCreditModal
                  store={transferCreditModalStore}
                  onSubmit={handleSubmitTransferCredit}
                  ids={{
                    toId: data?.expenseBudgetItem?.creditAccount?.id ?? "",
                  }}
                  ignoreIds={
                    data?.expenseBudgetItem?.creditAccount?.id
                      ? [data.expenseBudgetItem.creditAccount.id]
                      : undefined
                  }
                />

                <Box px="4" pt="6">
                  {expenseItem.completed_at ? (
                    <CompletedSection
                      expenseItem={expenseItem}
                      onReopen={handleAction}
                    />
                  ) : (
                    <IncompletedSection
                      expenseItem={expenseItem}
                      onComplete={handleAction}
                    />
                  )}
                </Box>
              </React.Fragment>
            )}
          </LoadingOverlay>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalCompleteExpense);

type Row = NonNullable<
  ExpenseBudgetItemQuery["expenseBudgetItem"]
>["globalPrepaymentStats"][0];
